import { useLinked } from '@type-r/react';
import { FormSelect, Option } from 'lqx-react/lib';
import React, { useCallback } from 'react';
import { forwardRef, useRef, useState, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import NumberFormat from 'react-number-format';
import { getBuyersAsCommonParticipants, getSuppliersAsCommonParticipants } from '../api/companyParticipant';
import { DigiDateInput, asTimelessLinkedDate } from '../shared';
import { numberInputStyles } from './number-input';
import { globalStore } from 'api/store';
import { DigiAsyncSelect, DigiCurrencySelect } from './select';
import { hasRole } from 'src/app/services/can';
import { MultiSelectDropdown } from '../connections/components/multiselect-dropdown';

export const NumberWithDecimalEditor = forwardRef((props: any, ref) => {
    console.log(props.colDef);
    const classes = numberInputStyles();
    const refInput = useRef(null);
    const [inputValue, setInputValue] = useState(props.value);
    const ariaLabel = (props.colDef && props.colDef.headerName) || (props.colDef && props.colDef.field);
    const handleChange = e => {
        setInputValue(e.value);
    }
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return +inputValue;
            },
            afterGuiAttached: () => {
                refInput.current.focus();
                refInput.current.select();
            },
        };
    });

    return <NumberFormat
        aria-label={ariaLabel}
        getInputRef={el => refInput.current = el}
        value={inputValue}
        className={classes.numericInput}
        decimalScale={2}
        fixedDecimalScale={true}
        thousandSeparator={true}
        onValueChange={(values) => handleChange(values)} />;
});

export const NumberWithoutDecimalEditor = forwardRef((props: any, ref) => {
    const classes = numberInputStyles();
    const refInput = useRef(null);
    const [inputValue, setInputValue] = useState(props.value);
    const ariaLabel = (props.colDef && props.colDef.headerName) || (props.colDef && props.colDef.field);
    const handleChange = e => {
        setInputValue(e.value);
    }
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return +inputValue;
            },
            afterGuiAttached: () => {
                refInput.current.focus();
                refInput.current.select();
            },
        };
    });

    return <NumberFormat
        aria-label={ariaLabel}
        getInputRef={el => refInput.current = el}
        value={inputValue}
        className={classes.numericInput}
        thousandSeparator={true}
        decimalScale={0}
        onValueChange={(values) => handleChange(values)} />;
});

export const SelectCurrencyEditor = forwardRef((props: any, ref) => {
    const currencies = globalStore.currenciesLookup.items.models;
    const $inputValue = useLinked(props.value);
    const position = props.eGridCell.getBoundingClientRect();
    const [show, setShow] = useState(true);
    const ariaLabel = (props.colDef && props.colDef.headerName) || (props.colDef && props.colDef.field);
    const handleUnmount = () => {
        setShow(prev => !prev);
    }
    const handleChildClick = e => {
        e.stopPropagation();
    }
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return $inputValue.value;
            },
        };
    });

    return (
        show && ReactDOM.createPortal(
            <div style={{
                width: '100vw',
                height: '100vh',
                position: 'fixed',
                zIndex: 98,
                top: 0,
            }}
                onClick={handleUnmount}
            >
                <div style={{
                    top: position.y,
                    left: position.x,
                    position: 'absolute',
                    zIndex: 99,
                    width: position.width
                }}
                    onClick={handleChildClick}
                >
                    <DigiCurrencySelect
                        aria-label={ariaLabel}
                        isMulti={false}
                        options={currencies}
                        $value={$inputValue}
                    />
                </div>
            </div>, document.body)
    );
})

export const SelectUnitEditor = forwardRef((props: any, ref) => {
    const currencies = ["eaches", "pounds", "kilograms"];
    const $inputValue = useLinked(props.value);

    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return $inputValue.value;
            },
        };
    });

    return (
        <FormSelect data-qa-id="payment currency dropdown" label="" $value={$inputValue}  >
            {currencies.map(sm => <Option key={sm} value={sm}>{sm}</Option>)}
        </FormSelect>
    );
})

export const DateEditor = forwardRef((props: any, ref) => {
    const $inputValue = useLinked(props.value);
    const position = props.eGridCell.getBoundingClientRect();
    const [show, setShow] = useState(true);
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return $inputValue.value;
            },
        };
    });
    const handleUnmount = () => {
        setShow(prev => !prev);
    }
    const handleChildClick = e => {
        e.stopPropagation();
    }

    return show && ReactDOM.createPortal(
        <div style={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            zIndex: 98,
            top: 0,
        }}
            onClick={handleUnmount}
        >
            <div style={{
                top: position.y,
                left: position.x,
                position: 'absolute',
                zIndex: 99,
                width: position.width
            }}
                onClick={handleChildClick}
            >
                <DigiDateInput date $value={asTimelessLinkedDate($inputValue as any)} />
            </div>
        </div>, document.body);
})

export const SupplierMultiSelectDropdownEditor = forwardRef((props: any, ref) => {
    const $inputValue = useLinked(props.value);

    const getItems = useCallback(async (searchText) => {
        return (await getSuppliersAsCommonParticipants(searchText));
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return $inputValue.value;
            },
        };
    });
    const ariaLabel = (props.colDef && props.colDef.headerName) || (props.colDef && props.colDef.field);

    return hasRole('AssetFacilitator')
        ?
        <DigiAsyncSelect
            aria-label={ariaLabel}
            data-qa-id={`supplier`}
            getItems={getItems}
            $value={$inputValue}
            isCreater={hasRole('AssetFacilitator')}
            isAgGridNested={true}
        />
        :
        <MultiSelectDropdown
            aria-label={ariaLabel}
            styles={{ marginTop: 0, marginBottom: 0 }}
            getItems={getItems}
            selectedItems={$inputValue.value}
            handleChange={value => $inputValue.set(value)}
            isMultiple={false}
        />
});

export const BuyerMultiSelectDropdownEditor = forwardRef((props: any, ref) => {
    const $inputValue = useLinked(props.value);

    const getItems = useCallback(async (searchText) => {
        return (await getBuyersAsCommonParticipants(searchText));
    }, []);

    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                return $inputValue.value;
            },
        };
    });

    const ariaLabel = (props.colDef && props.colDef.headerName) || (props.colDef && props.colDef.field);

    return hasRole('AssetFacilitator')
        ?
        <DigiAsyncSelect
            aria-label={ariaLabel}
            data-qa-id={`buyer`}
            getItems={getItems}
            $value={$inputValue}
            isCreater={hasRole('AssetFacilitator')}
            isAgGridNested={true}
        />
        :
        <MultiSelectDropdown
            aria-label={ariaLabel}
            styles={{ marginTop: 0, marginBottom: 0 }}
            getItems={getItems}
            selectedItems={$inputValue.value}
            handleChange={value => $inputValue.set(value)}
            isMultiple={false}
        />
});